import React, { FC } from 'react'
import Button from 'transfix-ui/components/Button'
import SuggestLocation from 'transfix-ui/components/SuggestLocation'
import { Box, Typography } from 'transfix-ui/core'
import { FilterBase } from 'apps/fleet-planner/components/atoms/FilterBase'
import { useBrowserLocationContext } from 'apps/fleet-planner/context/BrowserLocationContext'
import { BookableShipmentSearchQueryParams } from 'apps/fleet-planner/types'
import { lookupStateName } from 'utils/getLocations'
import { UseMyLocationButton } from './UseMyLocationButton'

const NO_OP = () => {}

interface IPickupLocationFilterProps {
  /**
   * @default false
   */
  isSelected: boolean
  value: BookableShipmentSearchQueryParams
  onChange: (value: BookableShipmentSearchQueryParams) => void
  onOpen: () => void
  onClose: () => void
}

const getLocationText = (stateCode?: string | null, city?: string | null) => {
  if (city) {
    return `${city}, ${stateCode}`
  }
  if (stateCode) {
    return lookupStateName(stateCode)
  }
  return ''
}

export const PickupLocationFilter: FC<IPickupLocationFilterProps> = ({
  isSelected = false,
  onChange,
  onClose,
  onOpen,
  value: valueProp,
}) => {
  const { browserLocation, hasLocationName } = useBrowserLocationContext()
  const { cityName, stateCode } = browserLocation || {}
  const { filters } = valueProp || {}
  const { pickupCity: pickupCityProps, pickupStateCode: pickupStateCodeProps } = filters || {}

  const pickupLocationChangeHandler = ({
    city: pickupCity,
    state: pickupStateCode,
  }: {
    city?: string
    state?: string
  }) => {
    onChange({
      filters: {
        ...filters,
        pickupCity,
        pickupStateCode,
        pickupTimes: undefined,
      },
    })
    onClose()
  }

  const useMyLocationClickHandler = () => {
    if (cityName && stateCode) {
      pickupLocationChangeHandler({ city: cityName, state: stateCode })
    }
  }

  return (
    <FilterBase
      label='Pickup Location'
      dataTest='pickup-location-filter'
      onOpen={onOpen}
      onClose={NO_OP}
      useClickAway={false}
      renderEdit={
        <Box data-test='render-edit-block'>
          <Box mb={1} display='flex' alignItems='center'>
            <Typography variant='button'>Where are you empty?</Typography>
          </Box>
          <Box mb={2}>
            <SuggestLocation
              large
              countries={['US', 'CA', 'MX']}
              resultTypes={['City', 'State', 'PostalCode']}
              placeholder='Enter a city, state, or zip'
              value={{
                city: pickupCityProps || '',
                state: pickupStateCodeProps || '',
              }}
              onChange={pickupLocationChangeHandler}
              dataTest='pickup-location-input'
            />
          </Box>
          {hasLocationName && <UseMyLocationButton onClick={useMyLocationClickHandler} />}
          <Box>
            <Button onClick={onClose} color='secondary'>
              Close
            </Button>
          </Box>
        </Box>
      }
      renderDisplay={
        <Box data-test='render-display-block'>
          {pickupStateCodeProps ? (
            <Typography variant='button' color='primary'>
              {getLocationText(pickupStateCodeProps, pickupCityProps)}
            </Typography>
          ) : (
            <Typography variant='button' color='textSecondary'>
              Where are you empty?
            </Typography>
          )}
        </Box>
      }
      isSelected={isSelected}
    />
  )
}
